import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useArticleDrawer } from '~/hooks/use-article-drawer/useArticleDrawer';
import { useNewsStatesDashboard } from '~/hooks/use-news-states-dashboard/useNewsStatesDashboard';
import { TabsType } from '~/views/news/utils/utils';
import { ArticleDrawer } from '~/widgets/news-widget/ArticleDrawer';

import * as S from './NewsWidget.styles';
import { WatchlistNewsEmptyState } from './no-news-found/NoNewsFound';
import { WidgetContent } from './widget-content/WidgetContent';
import { WidgetHeader } from './widget-header/WidgetHeader';

export interface NewsCommonProps {
  isGrid: boolean;
}

export interface NewsWidgetProps extends NewsCommonProps {}

export const NewsWidget = ({ isGrid }: NewsCommonProps) => {
  useTranslation(['news', 'chart', 'analyze', 'countries', 'watchlistManager']);

  const [activeTab, setActiveTab] = useState(TabsType.MostRead);

  const { singleNews, setSingleNews, closeArticleDrawer } = useArticleDrawer();
  const { uniqueTags, prevRefetchTimestamp, activeNewsList, isLoading } =
    useNewsStatesDashboard(activeTab);

  const changeTab = (_: number, tabKey: TabsType) => {
    setActiveTab(tabKey);
  };

  const watchlistNewsEmptyState = useMemo(() => {
    if (activeTab === TabsType.Watchlist) {
      if (!uniqueTags.length) {
        return WatchlistNewsEmptyState.WatchlistEmpty;
      } else if (!activeNewsList?.length) {
        return WatchlistNewsEmptyState.WatchlistNewsEmpty;
      }
    }
    return null;
  }, [activeNewsList?.length, activeTab, uniqueTags.length]);

  return (
    <>
      <S.NewsWidgetWrapper data-testid="news-widget">
        <WidgetHeader activeTab={activeTab} onChangeTab={changeTab} />
        <WidgetContent
          isGrid={isGrid}
          activeTab={activeTab}
          watchlistNewsEmptyState={watchlistNewsEmptyState}
          isLoading={isLoading}
          newsList={activeNewsList}
          prevRefetchTimestamp={prevRefetchTimestamp}
          onSingleNewsClick={setSingleNews}
        />
      </S.NewsWidgetWrapper>
      {singleNews && (
        <ArticleDrawer
          newsId={singleNews.id}
          list={activeNewsList}
          onClick={setSingleNews}
          onClose={closeArticleDrawer}
        />
      )}
    </>
  );
};
